import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Dropdown,
  Modal,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Tooltip,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  EllipsisOutlined,
  InfoCircleOutlined,
  PlusOutlined,
  ReloadOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import paths from "routes/paths";
import olreadiaService from "services/olreadiaService";
import { useAuth } from "contexts/AuthContext";
import useBusinessLocationTypes from "hooks/olreadia/useBusinessLocationTypes";
import BusinessEntityDrawer from "./BusinessEntityDrawer";
import BusinessEntityDetailsDrawer from "./BusinessEntityDetailsDrawer";
import BusinessLineDrawer from "./BusinessLineDrawer";
import BusinessLocationDetailsDrawer from "./BusinessLocationDetailsDrawer";
import BusinessUserDrawer from "./BusinessUserDrawer";
import BusinessUserDetailsDrawer from "./BusinessUserDetailsDrawer";
import ServicesManagerPanel from "./ServicesManagerPanel";
import { formatEnumLabel } from "../constants";

const toMessage = (err, fallback) =>
  err?.response?.data?.message || err?.message || fallback;

const logAxiosError = (context, err) => {
  try {
    const status = err?.response?.status;
    const method = err?.config?.method;
    const url = err?.config?.url;
    const responseData = err?.response?.data;
    // Avoid logging headers (may contain auth tokens).
    // eslint-disable-next-line no-console
    console.groupCollapsed?.(`[${context}] request failed${status ? ` (${status})` : ""}`);
    // eslint-disable-next-line no-console
    console.log({ status, method, url, responseData, message: err?.message });
    // eslint-disable-next-line no-console
    console.groupEnd?.();
  } catch {
    // eslint-disable-next-line no-console
    console.error(`[${context}] request failed`, err);
  }
};

const statusColor = (status) => {
  if (!status) return "default";
  if (status === "ACTIVE" || status === "APPROVED") return "green";
  if (status === "DRAFT" || status === "PENDING_REVIEW") return "gold";
  if (status === "SUSPENDED") return "red";
  return "default";
};

export default function BusinessEntityDetailsPage() {
  const { businessEntityId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const actorId = user?.id || "";
  const { locationTypes, locationTypesLoading, fetchLocationTypes } =
    useBusinessLocationTypes();

  const [entity, setEntity] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const [entityDrawerOpen, setEntityDrawerOpen] = useState(false);
  const [entityDetailsOpen, setEntityDetailsOpen] = useState(false);
  const [entityDetailsLoading, setEntityDetailsLoading] = useState(false);
  const [lineDrawerOpen, setLineDrawerOpen] = useState(false);
  const [editingLine, setEditingLine] = useState(null);
  const [userDrawerOpen, setUserDrawerOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [activeTab, setActiveTab] = useState("lines");

  const [lines, setLines] = useState([]);
  const [linesPage, setLinesPage] = useState({ totalElements: 0 });
  const [linesPagination, setLinesPagination] = useState({
    current: 1,
    pageSize: 20,
  });
  const [linesLoading, setLinesLoading] = useState(false);
  const [locationDetailsOpen, setLocationDetailsOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);

  const [users, setUsers] = useState([]);
  const [usersPage, setUsersPage] = useState({ totalElements: 0 });
  const [usersPagination, setUsersPagination] = useState({
    current: 1,
    pageSize: 20,
  });
  const [usersLoading, setUsersLoading] = useState(false);
  const [userDetailsOpen, setUserDetailsOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const linesInFlightRef = React.useRef(new Map());
  const usersInFlightRef = React.useRef(new Map());

  const loadEntity = async () => {
    if (!businessEntityId) return null;
    setLoading(true);
    setError(null);
    setNotFound(false);
    try {
      const res = await olreadiaService.businessEntities.get(businessEntityId);
      // eslint-disable-next-line no-console
      console.log("[businessEntities.get] payload", res);
      // eslint-disable-next-line no-console
      console.log("[businessEntities.get] token present?", Boolean(sessionStorage.getItem("authToken")));
      setEntity(res);
      return res;
    } catch (err) {
      logAxiosError("businessEntities.get", err);
      const status = err?.response?.status;
      if (status === 404) {
        setNotFound(true);
        setError("Business entity not found.");
        setEntity(null);
        return null;
      }

      const message = toMessage(err, "Failed to load business entity");
      setError(message);
      setEntity(null);
      toast.error(message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const openEntityDetails = async () => {
    if (!businessEntityId) return;
    setEntityDetailsOpen(true);
    setEntityDetailsLoading(true);
    try {
      const fresh = await olreadiaService.businessEntities.get(businessEntityId);
      setEntity(fresh);
    } catch (err) {
      toast.error(toMessage(err, "Failed to load business entity details"));
    } finally {
      setEntityDetailsLoading(false);
    }
  };

  const loadLines = async (pag = linesPagination) => {
    if (!businessEntityId) return;
    const key = `${businessEntityId}:${pag.current}:${pag.pageSize}`;
    const existing = linesInFlightRef.current.get(key);
    if (existing) return existing;
    setLinesLoading(true);
    const promise = (async () => {
      const res = await olreadiaService.businessEntities.locations.list(
        businessEntityId,
        { page: pag.current - 1, size: pag.pageSize },
      );
      const content = Array.isArray(res?.content) ? res.content : [];
      setLines(content);
      setLinesPage({ totalElements: res?.totalElements ?? content.length });
      // eslint-disable-next-line no-console
      console.debug("[businessEntities.locations.list] sample", content?.[0]);
    })()
      .catch((err) => {
        toast.error(toMessage(err, "Failed to load business lines"));
        throw err;
      })
      .finally(() => {
        linesInFlightRef.current.delete(key);
        setLinesLoading(false);
      });
    linesInFlightRef.current.set(key, promise);
    return promise;
  };

  const loadUsers = async (pag = usersPagination) => {
    if (!businessEntityId) return;
    const key = `${businessEntityId}:${pag.current}:${pag.pageSize}`;
    const existing = usersInFlightRef.current.get(key);
    if (existing) return existing;
    setUsersLoading(true);
    const promise = (async () => {
      const res = await olreadiaService.businessEntities.users.list(
        businessEntityId,
        { page: pag.current - 1, size: pag.pageSize },
      );
      const content = Array.isArray(res?.content) ? res.content : [];
      setUsers(content);
      setUsersPage({ totalElements: res?.totalElements ?? content.length });
    })()
      .catch((err) => {
        toast.error(toMessage(err, "Failed to load business users"));
        throw err;
      })
      .finally(() => {
        usersInFlightRef.current.delete(key);
        setUsersLoading(false);
      });
    usersInFlightRef.current.set(key, promise);
    return promise;
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!businessEntityId || businessEntityId === "undefined" || businessEntityId === "null") {
        setEntity(null);
        setError("Invalid business entity id in URL.");
        return;
      }
      fetchLocationTypes();
      const loaded = await loadEntity();
      if (cancelled) return;
      if (loaded) {
        setLinesLoading(true);
        setUsersLoading(true);
        await Promise.all([loadLines(), loadUsers()]);
      } else {
        setLinesLoading(false);
        setUsersLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    }, [businessEntityId]);

  const handleEntitySave = async (payload) => {
    setLoading(true);
    try {
      await olreadiaService.businessEntities.update(businessEntityId, payload, actorId);
      toast.success("Business entity updated");
      setEntityDrawerOpen(false);
      await loadEntity();
    } catch (err) {
      toast.error(toMessage(err, "Failed to update business entity"));
    } finally {
      setLoading(false);
    }
  };

  const handleEntityRemove = () => {
    Modal.confirm({
      title: "Remove business entity?",
      content: "This will deactivate/remove the business entity.",
      okText: "Remove",
      okButtonProps: { danger: true },
      onOk: async () => {
        setLoading(true);
        try {
          await olreadiaService.businessEntities.delete(businessEntityId, actorId);
          toast.success("Business entity removed");
          navigate(paths.businessEntities);
        } catch (err) {
          toast.error(toMessage(err, "Failed to remove business entity"));
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const handleLineSubmit = async (payload) => {
    setLoading(true);
    try {
      // eslint-disable-next-line no-console
      console.debug("[businessLocations.submit] payload", payload);
      if (editingLine?.id) {
        await olreadiaService.businessEntities.locations.update(
          businessEntityId,
          editingLine.id,
          payload,
          actorId,
        );
        toast.success("Business line updated");
      } else {
        await olreadiaService.businessEntities.locations.create(
          businessEntityId,
          payload,
          actorId,
        );
        toast.success("Business line created");
      }
      setLineDrawerOpen(false);
      setEditingLine(null);
      await loadLines();
      await loadEntity();
    } catch (err) {
      toast.error(toMessage(err, "Failed to save business line"));
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleLineRemove = (record) => {
    Modal.confirm({
      title: "Remove business line?",
      content: "This will deactivate/remove the business line.",
      okText: "Remove",
      okButtonProps: { danger: true },
      onOk: async () => {
        setLoading(true);
        try {
          await olreadiaService.businessEntities.locations.delete(
            businessEntityId,
            record.id,
            actorId,
          );
          toast.success("Business line removed");
          await loadLines();
          await loadEntity();
        } catch (err) {
          toast.error(toMessage(err, "Failed to remove business line"));
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const handleUserSubmit = async (payload) => {
    setLoading(true);
    try {
      if (editingUser?.id) {
        await olreadiaService.businessEntities.users.update(
          businessEntityId,
          editingUser.userId,
          {
            role: payload.role,
            status: payload.status,
            primaryContact: payload.primaryContact,
          },
          actorId,
        );
        toast.success("Business user updated");
      } else {
        await olreadiaService.businessEntities.users.add(
          businessEntityId,
          payload,
          actorId,
        );
        toast.success("Business user added");
      }
      setUserDrawerOpen(false);
      setEditingUser(null);
      await loadUsers();
      await loadEntity();
    } catch (err) {
      toast.error(toMessage(err, "Failed to save business user"));
    } finally {
      setLoading(false);
    }
  };

  const handleUserRemove = (record) => {
    Modal.confirm({
      title: "Remove business user?",
      content: "This will remove the user from the business entity.",
      okText: "Remove",
      okButtonProps: { danger: true },
      onOk: async () => {
        setLoading(true);
        try {
          await olreadiaService.businessEntities.users.remove(
            businessEntityId,
            record.userId,
            actorId,
          );
          toast.success("Business user removed");
          await loadUsers();
          await loadEntity();
        } catch (err) {
          toast.error(toMessage(err, "Failed to remove business user"));
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const openLocationDetails = (record) => {
    setSelectedLocation(record);
    setLocationDetailsOpen(true);
  };

  const openUserDetails = (record) => {
    setSelectedUser(record);
    setUserDetailsOpen(true);
  };

  const lineColumns = [
    { title: "Name", dataIndex: "name", key: "name", ellipsis: true },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (val) => <Tag color={statusColor(val)}>{formatEnumLabel(val)}</Tag>,
    },
    {
      title: "Location Type",
      key: "type",
      render: (_, r) => r?.businessLocationType?.label || r?.businessLocationType?.code || "-",
    },
    {
      title: "Location",
      key: "location",
      render: (_, r) => {
        const address1 = r?.locationInformation?.addressLine1;
        const city = r?.locationInformation?.city;
        const country = r?.locationInformation?.country;
        const primary = [city, country].filter(Boolean).join(", ");
        return address1 || primary || "-";
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <div className="be-actions" onClick={(e) => e.stopPropagation()}>
          <Dropdown
            trigger={["click"]}
            menu={{
              items: [
                {
                  key: "edit",
                  label: "Edit",
                  icon: <EditOutlined />,
                  onClick: ({ domEvent }) => {
                    domEvent.stopPropagation();
                    setEditingLine(record);
                    setLineDrawerOpen(true);
                  },
                },
                {
                  key: "remove",
                  label: "Remove",
                  icon: <DeleteOutlined />,
                  danger: true,
                  onClick: ({ domEvent }) => {
                    domEvent.stopPropagation();
                    handleLineRemove(record);
                  },
                },
              ],
            }}
          >
            <Button size="small" type="text" aria-label="Actions">
              <EllipsisOutlined style={{ fontSize: 18 }} />
            </Button>
          </Dropdown>
        </div>
      ),
    },
  ];

  const userColumns = [
    {
      title: "Name",
      key: "name",
      render: (_, r) => {
        const name = [r.firstName, r.lastName].filter(Boolean).join(" ");
        return name || "-";
      },
    },
    { title: "Email", dataIndex: "email", key: "email", ellipsis: true },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      render: (val) => formatEnumLabel(val) || "-",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (val) => <Tag color={statusColor(val)}>{formatEnumLabel(val)}</Tag>,
    },
    {
      title: "Primary",
      dataIndex: "primaryContact",
      key: "primaryContact",
      render: (val) => <Tag color={val ? "green" : "default"}>{val ? "Yes" : "No"}</Tag>,
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <div className="be-actions" onClick={(e) => e.stopPropagation()}>
          <Dropdown
            trigger={["click"]}
            menu={{
              items: [
                {
                  key: "edit",
                  label: "Edit",
                  icon: <EditOutlined />,
                  onClick: ({ domEvent }) => {
                    domEvent.stopPropagation();
                    setEditingUser({
                      id: record.id,
                      userId: record.userId,
                      email: record.email,
                      role: record.role,
                      status: record.status,
                      primaryContact: record.primaryContact,
                    });
                    setUserDrawerOpen(true);
                  },
                },
                {
                  key: "remove",
                  label: "Remove",
                  icon: <DeleteOutlined />,
                  danger: true,
                  onClick: ({ domEvent }) => {
                    domEvent.stopPropagation();
                    handleUserRemove(record);
                  },
                },
              ],
            }}
          >
            <Button size="small" type="text" aria-label="Actions">
              <EllipsisOutlined style={{ fontSize: 18 }} />
            </Button>
          </Dropdown>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-2 mt-2 gap-3">
        <WidgetSectionTitle
          icon="building"
          title={entity?.name || "Business Entity"}
          subtitle= "Manage business lines, attached services, and linked users."  
          className="mb-2 mt-2 flex-1"
        />
        <Space>
          <Tooltip title="View business entity details">
            <Button
              icon={<InfoCircleOutlined />}
              disabled={!entity?.id || loading}
              onClick={openEntityDetails}
            >
              Details
            </Button>
          </Tooltip>
          <Button type="primary" onClick={() => navigate(paths.businessEntities)}>
            Go Back
          </Button>
        </Space>
      </div>

      {error ? (
        <Alert
          type={notFound ? "warning" : "error"}
          showIcon
          message={notFound ? "Not found" : "Failed to load"}
          description={error}
          style={{ marginBottom: 12 }}
          action={
            <Space>
              <Button size="small" onClick={() => navigate(paths.businessEntities)}>
                Back to list
              </Button>
              <Button size="small" onClick={loadEntity}>
                Retry
              </Button>
            </Space>
          }
        />
      ) : null}

      {error && !entity ? null : (
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: "lines",
            label: "Business Locations",
            children: (
              <div>
                <div
                  className="d-flex justify-content-end"
                  style={{ marginBottom: 12 }}
                >
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    disabled={!entity}
                    onClick={() => {
                      setEditingLine(null);
                      setLineDrawerOpen(true);
                    }}
                  >
                    New Location
                  </Button>
                </div>
                <Table
                  rowKey="id"
                  loading={loading || linesLoading}
                  columns={lineColumns}
                  dataSource={lines}
                  className="custom-table"
                  bordered
                  tableLayout="auto"
                  scroll={{ x: "max-content" }}
                  size="small"
                  pagination={{
                    ...linesPagination,
                    total: linesPage.totalElements,
                    showSizeChanger: true,
                  }}
                  onChange={(pag) => {
                    const next = { current: pag.current, pageSize: pag.pageSize };
                    setLinesPagination(next);
                    loadLines(next);
                  }}
                  onRow={(record) => ({
                    onClick: (e) => {
                      if (e?.target?.closest?.(".be-actions")) return;
                      openLocationDetails(record);
                    },
                  })}
                />
              </div>
            ),
          },
          {
            key: "services",
            label: "Business Offerings",
            children: (
              <div>
                <ServicesManagerPanel
                  businessEntityId={businessEntityId}
                />
              </div>
            ),
          },
          {
            key: "users",
            label: (
              <span>
                <TeamOutlined /> Business Users
              </span>
            ),
            children: (
              <div>
                <div
                  className="d-flex justify-content-end"
                  style={{ marginBottom: 12 }}
                >
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    disabled={!entity}
                    onClick={() => {
                      setEditingUser(null);
                      setUserDrawerOpen(true);
                    }}
                  >
                    Add User
                  </Button>
                </div>
                <Table
                  rowKey="id"
                  loading={loading || usersLoading}
                  columns={userColumns}
                  dataSource={users}
                  className="custom-table"
                  bordered
                  tableLayout="auto"
                  scroll={{ x: "max-content" }}
                  size="small"
                  pagination={{
                    ...usersPagination,
                    total: usersPage.totalElements,
                    showSizeChanger: true,
                  }}
                  onChange={(pag) => {
                    const next = { current: pag.current, pageSize: pag.pageSize };
                    setUsersPagination(next);
                    loadUsers(next);
                  }}
                  onRow={(record) => ({
                    onClick: (e) => {
                      if (e?.target?.closest?.(".be-actions")) return;
                      openUserDetails(record);
                    },
                  })}
                />
              </div>
            ),
          },
        ]}
      />
      )}

      <BusinessEntityDrawer
        open={entityDrawerOpen}
        onClose={() => setEntityDrawerOpen(false)}
        onSubmit={handleEntitySave}
        loading={loading}
        initialValues={entity}
        locationTypes={locationTypes}
        locationTypesLoading={locationTypesLoading}
      />

      <BusinessLineDrawer
        open={lineDrawerOpen}
        onClose={() => {
          setLineDrawerOpen(false);
          setEditingLine(null);
        }}
        onSubmit={handleLineSubmit}
        loading={loading}
        initialValues={editingLine}
        locationTypes={locationTypes}
        locationTypesLoading={locationTypesLoading}
      />

      <BusinessLocationDetailsDrawer
        open={locationDetailsOpen}
        location={selectedLocation}
        onClose={() => setLocationDetailsOpen(false)}
        onEdit={(record) => {
          setLocationDetailsOpen(false);
          setEditingLine(record);
          setLineDrawerOpen(true);
        }}
      />

      <BusinessUserDrawer
        open={userDrawerOpen}
        onClose={() => {
          setUserDrawerOpen(false);
          setEditingUser(null);
        }}
        onSubmit={handleUserSubmit}
        loading={loading}
        initialValues={editingUser}
      />

      <BusinessUserDetailsDrawer
        open={userDetailsOpen}
        user={selectedUser}
        onClose={() => setUserDetailsOpen(false)}
        onEdit={(record) => {
          setUserDetailsOpen(false);
          setEditingUser({
            id: record?.id,
            userId: record?.userId,
            email: record?.email,
            role: record?.role,
            status: record?.status,
            primaryContact: record?.primaryContact,
          });
          setUserDrawerOpen(true);
        }}
      />

      <BusinessEntityDetailsDrawer
        open={entityDetailsOpen}
        entity={entity}
        loading={entityDetailsLoading}
        onClose={() => setEntityDetailsOpen(false)}
        onEdit={() => {
          setEntityDetailsOpen(false);
          setEntityDrawerOpen(true);
        }}
      />
    </div>
  );
}
